import React, { useEffect, useState } from "react";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Modal,
  LoadingWrapper,
  LoadingButton,
} from "components/ui";
import { useToast } from "hooks/useToast";
import { useLoading } from "hooks/useLoading";
import { usePermission } from "hooks/usePermissions";
import { PageId, ActionCode } from "types/index";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import {
  AddOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { usersService } from "services/users.service";

type UserListItem = {
  id: string | number;
  name: string;
  email: string;
  createdTimeStr?: string;
  companyId: number;
};

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Removed manual loading since global API loading handles it
  const {
    startLoading: startDeleteLoading,
    stopLoading: stopDeleteLoading,
    isLoading: isDeleting,
  } = useLoading({
    operationId: "users-delete",
    message: "Deleting user...",
    type: "button",
  });

  const canView = usePermission(PageId.USERS, ActionCode.VIEW);
  const canAdd = usePermission(PageId.USERS, ActionCode.ADD);
  const canEdit = usePermission(PageId.USERS, ActionCode.EDIT);
  const canDelete = usePermission(PageId.USERS, ActionCode.DELETE);

  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [rows, setRows] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = rows;
  const columnCount = 5;

  useEffect(() => {
    const fetchData = async () => {
      setError(null);
      try {
        const res = await usersService.list(page, pageSize);
        const normalized = (res.data || []).map((u: any) => ({
          id: u.id,
          name: u.name ?? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim(),
          email: u.email ?? u.username ?? "",
          createdTimeStr: u.createdTimeStr ?? u.createdAt ?? "",
          companyId: u.companyId ?? "",
        })) as UserListItem[];
        setRows(normalized);
        setTotal(res.pagination.total);
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e?.message || friendly);
      }
    };
    fetchData();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const onAdd = () => {
    navigate(ROUTES.USER_NEW);
  };

  const onView = (id: string) => {
    navigate(`${ROUTES.USERS}/${id}/view`);
  };

  const onRequestDelete = (id: string) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!deletingId) return;
    startDeleteLoading();
    try {
      await usersService.remove(deletingId);
      const res = await usersService.list(page, pageSize);
      const normalized = (res.data || []).map((u: any) => ({
        id: u.id,
        name: u.name ?? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim(),
        email: u.email ?? u.username ?? "",
        companyId: u.companyId ?? "",
        createdTimeStr: u.createdTimeStr ?? u.createdAt ?? "",
      })) as UserListItem[];
      setRows(normalized);
      setTotal(res.pagination.total);
      toast.success("User deleted successfully");
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      toast.error(e?.message || friendly);
    } finally {
      stopDeleteLoading();
      setConfirmOpen(false);
      setDeletingId(null);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader title="Users" />
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {canAdd && (
                <Button variant="primary" size="sm" onClick={onAdd} className="w-full sm:w-auto">
                  <AddOutlined fontSize="small" />
                  <span>Add</span>
                </Button>
              )}
            </div>
          </div>

          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

          <LoadingWrapper operationId="users-list" type="inline">
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {!error && pagedRows.length === 0 && (
                <div className="p-6 text-center text-secondary-600">No data</div>
              )}
              {pagedRows.map((u) => (
                <div
                  key={u.id}
                  className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-secondary-900 text-base">
                        {u.name}
                      </h3>
                      <p className="text-sm text-secondary-600 mt-1 break-all">
                        {u.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {canView && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onView(String(u.id))}
                          className="p-2"
                        >
                          <VisibilityOutlined fontSize="small" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`${ROUTES.USERS}/${String(u.id)}/edit`)}
                          className="p-2"
                        >
                          <EditOutlined fontSize="small" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 font-medium min-w-[100px]">
                        Type:
                      </span>
                      <span className="text-secondary-900">
                        {typeof u.companyId === "number" ? "Company" : "Admin"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 font-medium min-w-[100px]">
                        Created:
                      </span>
                      <span className="text-secondary-900">
                        {u.createdTimeStr || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="text-left p-3 text-secondary-700 font-semibold">
                      Name
                    </th>
                    <th className="text-left p-3 text-secondary-700 font-semibold">
                      Email
                    </th>
                    <th className="text-left p-3 text-secondary-700 font-semibold">
                      Type
                    </th>
                    <th className="text-left p-3 text-secondary-700 font-semibold">
                      Created Time
                    </th>
                    <th className="text-left p-3 text-secondary-700 font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!error && pagedRows.length === 0 && (
                    <tr>
                      <td
                        className="p-6 text-center text-secondary-600"
                        colSpan={columnCount}
                      >
                        No data
                      </td>
                    </tr>
                  )}
                  {pagedRows.map((u) => (
                    <tr
                      key={u.id}
                      className="border-t border-secondary-100 hover:bg-secondary-50"
                    >
                      <td className="p-3">{u.name}</td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3">
                        {typeof u.companyId === "number" ? "Company" : "Admin"}
                      </td>
                      <td className="p-3">{u.createdTimeStr || "-"}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {canView && (
                            <IconButton
                              size="small"
                              onClick={() => onView(String(u.id))}
                              aria-label="View"
                            >
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          )}
                          {canEdit && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(`${ROUTES.USERS}/${u.id}/edit`)
                              }
                              aria-label="Edit"
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          )}
                          {canDelete && (
                            <IconButton
                              size="small"
                              onClick={() => onRequestDelete(String(u.id))}
                              aria-label="Delete"
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </LoadingWrapper>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={page === 1}
              className="w-full sm:w-auto"
            >
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of{" "}
              {total}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={page === totalPages}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={confirmOpen}
        title="Delete User"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={onConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      >
        <LoadingButton
          isLoading={isDeleting}
          loadingText="Deleting..."
          className="hidden"
        >
          Delete
        </LoadingButton>
        Are you sure you want to delete this user?
      </Modal>
    </MainLayout>
  );
};

export default UsersPage;
